"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { graphql, readFragment, useQuery } from "@opensea/graphql";
import { useToasts } from "@opensea/ui-kit";
import { useLocalStorage } from "@opensea/ui-kit/hooks";
import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { isItemTradable, isItemTradableFragment } from "../../../../../../apps/web/components/items/validation/tradable";
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants";
import { useSyncFormFields } from "../../../hooks/useSyncFormField";
import { offerFlowSchema } from "../../../lib/zod/schema";
import { useAddress } from "../../../polychain/hooks/useAddress";
import { FlowActions, FlowState } from "../../../stores/flowStore";
import { useEndFlowOnAccountChange } from "../../hooks/useEndFlowOnAccountChange";
import { useLiveUpdateItemsBestOrders, useLiveUpdateItemsBestOrdersFragment } from "../../hooks/useLiveUpdateItemsBestOrders";
import { CreateOfferToast } from "../../ToastHandler/CreateOfferToast/CreateOfferToast";
import { DEFAULT_EXPIRATION, OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE, OFFER_EXPIRATION_LOCAL_STORAGE } from "../constants";
import { CreateOfferFlowReview } from "./components/CreateOfferFlowReview";
import { CreateOfferFlowReview_itemFragment } from "./components/CreateOfferFlowReview/CreateOfferFlowReview.fragment";
import { CreateOfferForm, CreateOfferFormSkeleton } from "./components/CreateOfferForm";
import { CreateOfferForm_itemFragment } from "./components/CreateOfferForm/CreateOfferForm.fragment";
import { CreateOfferPriceWarningModal } from "./components/CreateOfferPriceWarningModal";
import { CreateOfferPriceWarningModal_itemFragment } from "./components/CreateOfferPriceWarningModal/CreateOfferPriceWarningModal.fragment";
const CreateOfferFlowBaseFragment = graphql(`
    fragment CreateOfferFlowBase on Item {
      id
      tokenId
      contractAddress
      chain {
        identifier
        wrappedNativeCurrency {
          address
        }
        offerCurrency {
          symbol
          address
        }
        nativeCurrency {
          symbol
        }
      }
      imageUrl
      collection {
        id
        fees {
          totalCreatorFee {
            isRequired
            feeBasisPoints
          }
          openseaFee {
            feeBasisPoints
          }
        }
        floorPrice {
          id
          pricePerItem {
            token {
              unit
            }
          }
        }
      }
      bestOffer {
        id
        pricePerItem {
          token {
            unit
          }
        }
      }
      ...CreateOfferForm_item
      ...CreateOfferFlowReview_item
      ...useLiveUpdateItemsBestOrders
      ...CreateOfferPriceWarningModal_item
      ...isItemTradable
    }
  `, [CreateOfferForm_itemFragment, CreateOfferFlowReview_itemFragment, useLiveUpdateItemsBestOrdersFragment, CreateOfferPriceWarningModal_itemFragment, isItemTradableFragment]);
const CreateOfferFlowQuery = graphql(`
    query CreateOfferFlowQuery(
      $identifiers: [ItemIdentifierInput!]!
    ) {
      itemsByIdentifiers(identifiers: $identifiers) {
        id
        tokenId
        contractAddress
        chain {
          identifier
        }
        ...CreateOfferFlowBase
        ...isItemTradable
      }
    }
  `, [CreateOfferFlowBaseFragment, isItemTradableFragment]);
export function CreateOfferFlow(t0) {
  const $ = _c(11);
  const {
    items: itemsInput
  } = t0;
  useAddress();
  let t1;
  if ($[0] !== itemsInput) {
    t1 = itemsInput.map(_temp);
    $[0] = itemsInput;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      query: CreateOfferFlowQuery,
      variables: {
        identifiers: t1
      },
      context: NETWORK_ONLY_OPERATION_CONTEXT
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [t3] = useQuery(t2);
  const {
    data,
    fetching
  } = t3;
  useEndFlowOnAccountChange();
  const anyItemDisabled = data?.itemsByIdentifiers.some(_temp2);
  if (anyItemDisabled) {
    throw new Error("One or more items disabled for trading.");
  }
  let t4;
  if ($[4] !== data?.itemsByIdentifiers || $[5] !== itemsInput) {
    t4 = data?.itemsByIdentifiers.flatMap(item_1 => {
      const quantity = itemsInput.find(itemInput => itemInput.tokenId === item_1?.tokenId && itemInput.chain.identifier === item_1.chain.identifier && itemInput.contractAddress === item_1.contractAddress)?.quantity;
      return item_1 && quantity ? [{
        ...item_1,
        quantity
      }] : [];
    });
    $[4] = data?.itemsByIdentifiers;
    $[5] = itemsInput;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const items = t4;
  if (fetching) {
    let t5;
    if ($[7] !== itemsInput.length) {
      t5 = <CreateOfferFormSkeleton count={itemsInput.length} />;
      $[7] = itemsInput.length;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  if (!items) {
    throw new Error("There was a problem with creating offers.");
  }
  let t5;
  if ($[9] !== items) {
    t5 = <CreateOfferFlowBase items={items} />;
    $[9] = items;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function _temp2(item_0) {
  return !isItemTradable(item_0);
}
function _temp(item) {
  return {
    tokenId: item.tokenId,
    contractAddress: item.contractAddress,
    chain: item.chain.identifier
  };
}
function CreateOfferFlowBase(t0) {
  const $ = _c(43);
  const {
    items: itemKeys
  } = t0;
  const {
    addCustomToast
  } = useToasts();
  const [view, setView] = useState("create");
  const [isPriceWarningModalOpen, setIsPriceWarningModalOpen] = useState(false);
  let t1;
  if ($[0] !== itemKeys) {
    t1 = itemKeys.map(_temp3);
    $[0] = itemKeys;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const items = useLiveUpdateItemsBestOrders(t1);
  const t2 = useDefaultFormValues(items);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = zodResolver(offerFlowSchema);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2) {
    t4 = {
      defaultValues: t2,
      mode: "onChange",
      resolver: t3
    };
    $[3] = t2;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const methods = useForm(t4);
  let t5;
  if ($[5] !== methods.control) {
    t5 = {
      control: methods.control,
      name: "offers"
    };
    $[5] = methods.control;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const offers = useWatch(t5);
  let t6;
  let t7;
  if ($[7] !== items || $[8] !== offers) {
    let t8;
    if ($[10] !== offers) {
      t8 = item_0 => offers.find(offer => offer.id === item_0.id);
      $[10] = offers;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    t7 = items.filter(t8);
    $[7] = items;
    $[8] = offers;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  t6 = t7;
  const offerItems = t6;
  let t8;
  if ($[12] !== offers) {
    t8 = offers.some(_temp4);
    $[12] = offers;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const shouldConfirmPrice = t8;
  let t9;
  if ($[14] !== items) {
    t9 = items.flatMap(_temp5);
    $[14] = items;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== methods.setValue || $[17] !== t9) {
    t10 = {
      fields: t9,
      setValue: methods.setValue
    };
    $[16] = methods.setValue;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  useSyncFormFields(t10);
  let t11;
  if ($[19] !== shouldConfirmPrice) {
    t11 = () => {
      if (shouldConfirmPrice) {
        setIsPriceWarningModalOpen(true);
      } else {
        setView("review");
      }
    };
    $[19] = shouldConfirmPrice;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const handleFormSubmit = t11;
  let t12;
  if ($[21] !== addCustomToast || $[22] !== offerItems || $[23] !== offers[0]?.offerSymbol || $[24] !== offers[0]?.price) {
    t12 = success => {
      const {
        receiptStatus
      } = FlowState;
      addCustomToast(() => <CreateOfferToast imageUrls={offerItems.map(_temp6)} price={{
        unit: offers[0]?.price || 0,
        symbol: offers[0]?.offerSymbol || ""
      }} status={receiptStatus ?? "FAILED"} success={success} />, {
        id: "create-offer-toast",
        duration: 15000
      });
    };
    $[21] = addCustomToast;
    $[22] = offerItems;
    $[23] = offers[0]?.offerSymbol;
    $[24] = offers[0]?.price;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const showToast = t12;
  const firstItem = items[0];
  let t13;
  if ($[26] !== handleFormSubmit || $[27] !== items || $[28] !== view) {
    t13 = view === "create" && <CreateOfferForm items={items} onSubmit={handleFormSubmit} />;
    $[26] = handleFormSubmit;
    $[27] = items;
    $[28] = view;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== items || $[31] !== showToast || $[32] !== view) {
    t14 = view === "review" && <CreateOfferFlowReview items={items} onBack={() => setView("create")} onError={() => {
      FlowActions.setReceiptStatus("FAILED");
      showToast(false);
      FlowActions.endFlow();
    }} onSuccess={() => {
      FlowActions.setReceiptStatus("SUCCESS");
      FlowActions.onSuccess();
      showToast(true);
      FlowActions.endFlow();
    }} />;
    $[30] = items;
    $[31] = showToast;
    $[32] = view;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setView("review");
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== firstItem || $[36] !== isPriceWarningModalOpen) {
    t16 = <CreateOfferPriceWarningModal item={firstItem} onNext={t15} open={isPriceWarningModalOpen} setIsOpen={setIsPriceWarningModalOpen} />;
    $[35] = firstItem;
    $[36] = isPriceWarningModalOpen;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== methods || $[39] !== t13 || $[40] !== t14 || $[41] !== t16) {
    t17 = <FormProvider {...methods}>{t13}{t14}{t16}</FormProvider>;
    $[38] = methods;
    $[39] = t13;
    $[40] = t14;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  return t17;
}
function _temp6(item_2) {
  return item_2.imageUrl || "";
}
function _temp5(item_1, index) {
  return [{
    name: `offers.${index}.floorPrice`,
    value: item_1.collection?.floorPrice?.pricePerItem.token.unit
  }, {
    name: `offers.${index}.bestOffer`,
    value: item_1.bestOffer?.pricePerItem.token.unit
  }];
}
function _temp4(offer_0) {
  return offer_0.shouldConfirmPrice;
}
function _temp3(item) {
  return {
    ...readFragment(CreateOfferFlowBaseFragment, item),
    quantity: item.quantity
  };
}
const useDefaultFormValues = items => {
  const $ = _c(10);
  const [localStorageOfferExpirationMinutes] = useLocalStorage(OFFER_EXPIRATION_LOCAL_STORAGE, DEFAULT_EXPIRATION);
  const [localStorageOfferExpirationCustom] = useLocalStorage(OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE, undefined);
  let t0;
  if ($[0] !== localStorageOfferExpirationCustom) {
    t0 = localStorageOfferExpirationCustom ? new Date(localStorageOfferExpirationCustom) : undefined;
    $[0] = localStorageOfferExpirationCustom;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== localStorageOfferExpirationMinutes || $[3] !== t0) {
    t1 = {
      expirationType: localStorageOfferExpirationMinutes,
      customExpiration: t0
    };
    $[2] = localStorageOfferExpirationMinutes;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== items) {
    t2 = items.map(_temp7);
    $[5] = items;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = {
      expiration: t1,
      offers: t2
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
function _temp7(item) {
  return {
    id: item.id,
    quantity: item.quantity,
    applyCreatorFee: Boolean(item.collection?.fees.totalCreatorFee.isRequired),
    isCreatorFeeRequired: Boolean(item.collection?.fees.totalCreatorFee.isRequired),
    creatorFeeBasisPoints: item.collection?.fees.totalCreatorFee.feeBasisPoints || 0,
    openSeaFeeBasisPoints: item.collection?.fees.openseaFee?.feeBasisPoints || 0,
    price: Number.NaN,
    floorPrice: item.collection?.floorPrice?.pricePerItem.token.unit,
    bestOffer: item.bestOffer?.pricePerItem.token.unit,
    offerSymbol: item.chain.offerCurrency.symbol,
    nativeCurrencySymbol: item.chain.nativeCurrency.symbol,
    isWrappedNativeCurrency: item.chain.offerCurrency.address === item.chain.wrappedNativeCurrency?.address
  };
}